export const DASHBOARD_SCOPES = Object.freeze([
  "level_system",
  "welcome_system",
  "automod_system",
  "logs_system",
  "embeds_system",
  "commands_system",
  "temp_voice_system",
]);

const OWNER_ACCESS_TYPES = new Set(["owner", "administrator", "admin"]);

export const normalizeAccessTypes = (accessTypes) => {
  const values = Array.isArray(accessTypes)
    ? accessTypes.map((item) => String(item || "").trim().toLowerCase()).filter(Boolean)
    : [];
  return Array.from(new Set(values));
};

export const normalizeScopes = (scopes) => {
  const values = Array.isArray(scopes)
    ? scopes.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  if (values.includes("*")) return ["*"];
  return Array.from(new Set(values));
};

export const buildDashboardAccessContext = ({ access_type = [], dashboard_scopes = [] } = {}) => {
  const accessTypes = normalizeAccessTypes(access_type);
  const scopes = normalizeScopes(dashboard_scopes);
  const ownerOrAdmin = accessTypes.some((accessType) => OWNER_ACCESS_TYPES.has(accessType));
  const fullAccess = ownerOrAdmin || scopes.includes("*");

  return {
    accessTypes,
    scopes,
    isOwnerOrAdmin: ownerOrAdmin,
    hasFullAccess: fullAccess,
  };
};

const resolveAccessContext = (contextOrRaw) => {
  if (contextOrRaw && typeof contextOrRaw === "object" && "isOwnerOrAdmin" in contextOrRaw) {
    return {
      accessTypes: Array.isArray(contextOrRaw.accessTypes) ? contextOrRaw.accessTypes : [],
      scopes: Array.isArray(contextOrRaw.scopes) ? contextOrRaw.scopes : [],
      isOwnerOrAdmin: Boolean(contextOrRaw.isOwnerOrAdmin),
      hasFullAccess: Boolean(contextOrRaw.hasFullAccess),
    };
  }
  return buildDashboardAccessContext(contextOrRaw || {});
};

export const isOwnerOrAdmin = (contextOrRaw) => resolveAccessContext(contextOrRaw).isOwnerOrAdmin;

export const hasScope = (contextOrRaw, scope) => {
  const context = resolveAccessContext(contextOrRaw);
  if (context.hasFullAccess) return true;
  const requiredScope = String(scope || "").trim();
  if (!requiredScope) return true;
  return context.scopes.includes(requiredScope);
};

export const canAccessRoute = (contextOrRaw, routeOrRule = null) => {
  const context = resolveAccessContext(contextOrRaw);
  if (!routeOrRule) return true;

  if (typeof routeOrRule === "string") {
    return hasScope(context, routeOrRule);
  }

  const ownerOnly = Boolean(routeOrRule.ownerOnly);
  const requiredScope = String(routeOrRule.requiredScope || "").trim();

  if (ownerOnly) return context.isOwnerOrAdmin;
  if (requiredScope) return hasScope(context, requiredScope);
  return true;
};
